import { useCallback, useContext, useMemo, useState } from 'react';
import { sort_center_api, getErrorMessageAsync } from '@shared/api';
import { SoundNotificationContext, useNotificationsV2 } from '@uni-space/uni-components';
import { printBase64PdfRouted, printPdfRouted } from '@shared/utils/printRouter';

type UseLabelPrinterOptions = {
  errorTitle?: string;
  fallbackErrorMessage?: string;
  codeMessageMap?: Record<string, string>;
};

/**
 * Куда печатать, решает маршрутизатор: выбран принтер в подвале sidebar — задание уходит
 * на облачную печать, иначе (и на любой ошибке отправки) прежний диалог print-js с модалкой.
 * Сюда управление доходит только с готовым PDF, поэтому ошибок печати здесь нет.
 */
function printPdfBlob(blob: Blob) {
  void printPdfRouted(blob, { showModal: true });
}

export function useLabelPrinter(options?: UseLabelPrinterOptions) {
  const { createNotification } = useNotificationsV2();
  const { onSoundError } = useContext(SoundNotificationContext);
  const [pendingCount, setPendingCount] = useState(0);

  const isLabelLoading = useMemo(() => pendingCount > 0, [pendingCount]);

  const start = useCallback(() => setPendingCount((c) => c + 1), []);
  const stop = useCallback(() => setPendingCount((c) => Math.max(0, c - 1)), []);

  const notifyError = useCallback(
    async (e: unknown) => {
      const fallback = options?.fallbackErrorMessage ?? 'Не удалось получить этикетку';
      const message = await getErrorMessageAsync(e, fallback, options?.codeMessageMap);
      createNotification({
        type: 'error',
        message: { title: options?.errorTitle ?? 'Ошибка', desc: message },
      });
      onSoundError();
    },
    [createNotification, onSoundError, options?.codeMessageMap, options?.errorTitle, options?.fallbackErrorMessage],
  );

  const run = useCallback(
    async <T,>(fn: () => Promise<T>) => {
      start();
      try {
        return await fn();
      } catch (e) {
        await notifyError(e);
        return undefined;
      } finally {
        stop();
      }
    },
    [notifyError, start, stop],
  );

  const printLabelFromBlob = useCallback((blob: Blob) => {
    printPdfBlob(blob);
  }, []);

  const printLabelFromFetcher = useCallback(
    async (fetcher: () => Promise<Blob>) => {
      const blob = await run(fetcher);
      if (blob) printPdfBlob(blob);
    },
    [run],
  );

  const printLabelFromUrl = useCallback(
    async (url: string) => {
      await printLabelFromFetcher(async () => {
        const response = await sort_center_api.get(url, { responseType: 'blob' });
        return new Blob([response.data], { type: 'application/pdf' });
      });
    },
    [printLabelFromFetcher],
  );

  const printLabelFromBase64 = useCallback(
    async (postalLabelBase64: string) => {
      await run(async () => {
        await printBase64PdfRouted(postalLabelBase64, { showModal: true });
      });
    },
    [run],
  );

  return { isLabelLoading, printLabelFromBlob, printLabelFromFetcher, printLabelFromUrl, printLabelFromBase64 };
}

